/**
 * Reclassification Service
 * Handles reclassification requests for previously classified documents
 */

import apiClient from 'services/apiClient';
import { API_ENDPOINTS } from 'config/api.config';
import { ReclassificationRequest, ReclassificationResponse } from 'types/chat.types';

/**
 * Reclassification Service Class
 */
class ReclassificationService {
  /**
   * Request reclassification for a document
   * @param documentId - The ID of the document to reclassify
   * @param userMessage - The user's feedback about what's missing or incorrect
   * @param reason - Optional reason for reclassification (defaults to 'User feedback - thumbs down')
   * @param usePreviousContext - Whether to use previous context (defaults to true)
   * @param conversationId - Optional conversation ID to associate with the reclassification
   * @returns Promise with reclassification response containing job_id
   */
  async requestReclassification(
    documentId: string,
    userMessage: string,
    reason: string = 'User feedback - thumbs down',
    usePreviousContext: boolean = true,
    conversationId?: string
  ): Promise<ReclassificationResponse> {
    console.log('[reclassificationService] requestReclassification called', {
      documentId,
      userMessage,
      reason,
      usePreviousContext,
      conversationId,
    });

    try {
      const payload: ReclassificationRequest = {
        reason,
        user_message: userMessage,
        use_previous_context: usePreviousContext,
        ...(conversationId && { conversation_id: conversationId }),
      };

      const response = await apiClient.post<ReclassificationResponse>(
        API_ENDPOINTS.RECLASSIFICATION.RECLASSIFY(documentId),
        payload
      );

      console.log('✅ [reclassificationService] Reclassification job created:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ [reclassificationService] Failed to request reclassification:', error);

      const errorMessage = error.response?.data?.detail ||
                          error.response?.data?.message ||
                          error.message ||
                          'Failed to request reclassification';

      throw new Error(errorMessage);
    }
  }

  /**
   * Get reclassification history for a document
   * @param documentId - The ID of the document
   * @returns Promise with classification history
   */
  async getClassificationHistory(documentId: string): Promise<any> {
    console.log('[reclassificationService] getClassificationHistory called', { documentId });

    try {
      const response = await apiClient.get(
        API_ENDPOINTS.RECLASSIFICATION.HISTORY(documentId)
      );

      console.log('✅ [reclassificationService] Classification history retrieved:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ [reclassificationService] Failed to get classification history:', error);

      const errorMessage = error.response?.data?.detail ||
                          error.response?.data?.message ||
                          error.message ||
                          'Failed to get classification history';

      throw new Error(errorMessage);
    }
  }
}

const reclassificationService = new ReclassificationService();
export default reclassificationService;
